import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faExclamationTriangle, faCrown } from '@fortawesome/free-solid-svg-icons';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AuthRequired from '../components/common/AuthRequired';
import AppHeader from '../components/common/AppHeader';
import BankInfoForm from '../components/user/BankInfoForm';
import ProfileHeader from '../components/profile/ProfileHeader';
import ProfileDetailsGrid from '../components/profile/ProfileDetailsGrid';
import BankInfoSection from '../components/profile/BankInfoSection';
import ProfileInfoNotice from '../components/profile/ProfileInfoNotice';
import PreferencesSection from '../components/profile/PreferencesSection';
import { useDarkMode } from '../hooks/useDarkMode';
import { useUnifiedProfile } from '../hooks/useUnifiedProfile';
import { useDisplayNameEditor } from '../hooks/useDisplayNameEditor';
import { useAuth } from '../hooks/useAuth';
import { BankInfo } from '../types';
import { useTranslation } from 'react-i18next';

const ProfilePage: React.FC = () => {
    const { t } = useTranslation();
    const { eventId, userId: urlUserId } = useParams<{ eventId?: string; userId?: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { darkMode, toggleDarkMode } = useDarkMode();
    const [showBankForm, setShowBankForm] = useState<boolean>(false);

    // Use unified profile hook
    const {
        profileData,
        eventName,
        loading,
        error,
        isEventContext,
        isViewingSelf,
        isCurrentUserOwner,
        accountDisplayName,
        permissions,
        updateDisplayName,
        updateBankInfo
    } = useUnifiedProfile(eventId, urlUserId);

    // Renaming yourself inside an event is the only case with a real choice:
    // this bill alone, the account (and every bill still using its name), or
    // back to the account name. Everywhere else the scope is already decided.
    const canChooseNameScope = Boolean(user && isEventContext && isViewingSelf);

    // Display name editing logic
    const displayNameEditor = useDisplayNameEditor({
        initialDisplayName: profileData?.displayName || '',
        accountDisplayName,
        canChooseScope: canChooseNameScope,
        defaultChoice: isEventContext ? 'event' : 'account',
        onSave: async (newName, scope) => {
            await updateDisplayName(newName, scope);
        }
    });

    // Bank info handlers
    const handleSaveBankInfo = async (bankInfo: BankInfo) => {
        // Errors propagate to BankInfoForm, which renders them inline
        await updateBankInfo(bankInfo);
        setShowBankForm(false);
    };

    const handleEditBankInfo = () => {
        setShowBankForm(true);
    };

    // Page metadata
    const getPageMetadata = () => {
        let pageTitle = t('profile.title');

        if (isEventContext) {
            if (isViewingSelf) {
                pageTitle = user ? t('profile.myAccountProfile') : t('profile.myProfile');
            } else {
                pageTitle = t('profile.participantProfile');
            }
        } else {
            pageTitle = t('profile.myProfile');
        }

        const pageSubtitle = isEventContext ?
            t(isViewingSelf ? 'profile.subtitles.yourProfileInEvent' : 'profile.subtitles.profileInEvent', { eventName }) :
            t('profile.subtitles.manageAccount');
        const backUrl = isEventContext ? `/event/${eventId}` : '/';

        return { pageTitle, pageSubtitle, backUrl };
    };

    const { pageTitle, pageSubtitle, backUrl } = getPageMetadata();

    // Check if viewing owner profile without permission
    const isOwnerWithoutPermission = profileData?.isOwner && !permissions.canEditDisplayName;

    // Loading state
    if (loading) {
        return <LoadingSpinner />;
    }

    // Error handling
    if (error || !profileData) {
        return (
            <div className="glass-page-bg flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-600 dark:text-red-400 mb-4">{error || t('profile.notFound')}</p>
                    <button
                        onClick={() => navigate(backUrl)}
                        className="glass-btn-primary inline-flex items-center"
                    >
                        <FontAwesomeIcon icon={faUser} className="mr-2" />
                        {isEventContext ? t('profile.backToEvent') : t('profile.backToHome')}
                    </button>
                </div>
            </div>
        );
    }

    // Auth check for non-event, non-authenticated users
    if (!isEventContext && !user) {
        return <AuthRequired message={t('profile.authRequired')} />;
    }

    return (
        <div className="glass-page-bg">
            {/* Header */}
            <AppHeader
                title={pageTitle}
                subtitle={pageSubtitle}
                icon={faUser}
                backTo={backUrl}
                darkMode={darkMode}
                onToggleDarkMode={toggleDarkMode}
            />

            {/* Main Content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Notification Banners */}
                {isCurrentUserOwner && !isViewingSelf && permissions.canEditDisplayName && (
                    <div className="glass-info-banner mb-6">
                        <div className="flex items-center">
                            <FontAwesomeIcon icon={faCrown} className="text-blue-600 dark:text-blue-400 mr-3" />
                            <div>
                                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                                    {t('profile.notifications.ownerPrivileges.title')}
                                </h3>
                                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                                    {t('profile.notifications.ownerPrivileges.message')}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {isOwnerWithoutPermission && (
                    <div className="glass-info-banner mb-6" style={{background: 'rgba(245, 158, 11, 0.08)', borderColor: 'rgba(245, 158, 11, 0.15)'}}>
                        <div className="flex items-center">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-600 dark:text-amber-400 mr-3" />
                            <div>
                                <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">
                                    {t('profile.notifications.limitedAccess.title')}
                                </h3>
                                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                                    {t('profile.notifications.limitedAccess.message')}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Single Profile Card */}
                <div className="glass-card overflow-hidden">
                    {/* Profile Header */}
                    <div className="p-8 border-b border-gray-200 dark:border-gray-700">
                        <ProfileHeader displayData={profileData} />
                    </div>

                    {/* Profile Details & Editable Fields */}
                    <div className="p-8">
                        <ProfileDetailsGrid
                            displayData={profileData}
                            permissions={permissions}
                            displayNameEditor={displayNameEditor}
                            isCurrentUserOwner={isCurrentUserOwner}
                            isViewingSelf={isViewingSelf}
                        />
                    </div>

                    {/* Bank Information */}
                    <div className="border-t border-gray-200 dark:border-gray-700 p-8">
                        <BankInfoSection
                            displayData={profileData}
                            canEdit={permissions.canEditBankInfo}
                            onEditBankInfo={handleEditBankInfo}
                            isOwnerEdit={isCurrentUserOwner && !isViewingSelf}
                        />
                    </div>

                    {/* Settings — the account's own, so only on your own profile,
                        and in an event too: that is where the menu leads from there */}
                    {user && isViewingSelf && (
                        <div className="border-t border-gray-200 dark:border-gray-700 p-8">
                            <PreferencesSection />
                        </div>
                    )}
                </div>

                {/* Additional Info Notice - Only for non-event context */}
                {!isEventContext && permissions.canEditDisplayName && (
                    <div className="mt-8">
                        <ProfileInfoNotice />
                    </div>
                )}
            </div>

            {/* Bank Info Form Modal */}
            {showBankForm && permissions.canEditBankInfo && (
                <BankInfoForm
                    currentBankInfo={profileData.bankInfo || null}
                    onSave={handleSaveBankInfo}
                    onCancel={() => setShowBankForm(false)}
                    loading={loading}
                />
            )}
        </div>
    );
};

export default ProfilePage;